import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { StoneType } from "@/types/stone-type/stone-type.types";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table/index";
import { TableHeader } from "@/components/ui/table-header/index";
import { Plus } from "lucide-react";
import { StoneTypeForm } from "./stone-types/StoneTypeForm";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog/index";
import { useStoneTypeTable } from "@/hooks/stone-types/useStoneTypeTable";
import { useStoneTypeColumns } from "@/components/setup/stone-types/columns";
import { useConfirmationDialog } from "@/hooks/stone-types/useConfirmationDialog";

export const StoneTypes = () => {
  const { t } = useLanguage();
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  const [formOpen, setFormOpen] = useState(false);
  const [selectedStoneType, setSelectedStoneType] = useState<StoneType | null>(null);

  const {
    filteredStoneTypes,
    loading,
    refetch,
    searchTerm,
    setSearchTerm,
    showArchived,
    setShowArchived,
    nameFilters,
    setNameFilters,
    clearFilters,
    nameOptions,
  } = useStoneTypeTable();

  const {
    isOpen: confirmDialogOpen,
    confirmAction,
    isLoading: isConfirmActionLoading,
    openDialog,
    closeDialog,
    handleConfirm,
  } = useConfirmationDialog(refetch);

  const handleAddStoneType = () => {
    setSelectedStoneType(null);
    setFormOpen(true);
  };

  const handleEditStoneType = (stoneType: StoneType) => {
    setSelectedStoneType(stoneType);
    setFormOpen(true);
  };

  const handleArchiveAction = (stoneType: StoneType) => {
    openDialog({
      type: stoneType.isArchived ? "restore" : "archive",
      stoneTypeId: stoneType.id,
      stoneTypeName: stoneType.name,
    });
  };

  const columns = useStoneTypeColumns(t, handleEditStoneType, handleArchiveAction);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const filters = [
    {
      label: t("stoneTypes.filters.name"),
      options: nameOptions,
      selectedValues: nameFilters,
      onChange: setNameFilters,
      showSearch: true,
    },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-lg font-semibold">{t("stoneTypes.title")}</h2>
        {!isAssimagraUser && (
          <Button
            onClick={handleAddStoneType}
            className="bg-teal-600 text-white hover:bg-teal-700 self-start sm:self-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("stoneTypes.addButton")}
          </Button>
        )}
      </div>

      <TableHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={t("stoneTypes.searchPlaceholder")}
        filters={filters}
        toggle={[
          {
            label: t("stoneTypes.showArchived"),
            checked: showArchived,
            onCheckedChange: setShowArchived,
          },
        ]}
        onClearAll={clearFilters}
      />

      <div className="overflow-hidden">
        <DataTable columns={columns} data={filteredStoneTypes} pageSize={10} className="w-full" minWidth="auto" />
      </div>

      <StoneTypeForm
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        stoneTypeToEdit={selectedStoneType}
        onSuccess={refetch}
      />

      {confirmAction && (
        <ConfirmationDialog
          isOpen={confirmDialogOpen}
          onClose={closeDialog}
          onConfirm={handleConfirm}
          title={
            confirmAction.type === "archive"
              ? t("stoneTypes.confirmArchive.title")
              : t("stoneTypes.confirmRestore.title")
          }
          description={t(
            confirmAction.type === "archive"
              ? "stoneTypes.confirmArchive.description"
              : "stoneTypes.confirmRestore.description"
          )}
          confirmText={
            isConfirmActionLoading
              ? t(
                  confirmAction.type === "archive"
                    ? "stoneTypes.confirmArchive.actionLoading"
                    : "stoneTypes.confirmRestore.actionLoading"
                )
              : t(
                  confirmAction.type === "archive"
                    ? "stoneTypes.confirmArchive.action"
                    : "stoneTypes.confirmRestore.action"
                )
          }
          cancelText={t("stoneTypes.cancel")}
          variant={confirmAction.type === "archive" ? "destructive" : "default"}
          isLoading={isConfirmActionLoading}
        />
      )}
    </div>
  );
};
